import { property, subclass } from '@arcgis/core/core/accessorSupport/decorators';
import { getLocale } from '@arcgis/core/intl';
import { messageBundle, storeNode, tsx } from '@arcgis/core/widgets/support/widget';
import Widget from '@arcgis/core/widgets/Widget';

import PublishAppViewModel from '../data/PublishApp/PublishAppViewModel';
import { isDarkMode } from '../utils/appUtils';
import { EAppTemplateType } from 'templates-common-library/baseClasses/CompatibilityChecker';
import { findEnvironmentByUrl } from '../utils/urlUtils';
import { AppTypeInfo, EResourceType, previewType, ResourceInfo, resourceItem } from './AppTypeTile';
import "./IFrameAppPreview/styles/IFrameAppPreview.scss";
import PublishForm from './PublishForm';

import staticAppConfig from "../../config/appConfig.json";
import { focusElement } from '../utils/focusUtils';
import { applyShadowStyle } from '../utils/utils';
import { SHADOW_STYLE_PREVIEW_MODAL } from '../utils/shadowStyles';

export interface IFrameAppPreviewProperties extends __esri.WidgetProperties {
  appTypeInfo: AppTypeInfo
  resourceInfo: ResourceInfo
  publishAppVM: PublishAppViewModel;
  previewType: previewType;
  existingApp?: __esri.PortalItem;
  sendFocusOnClose: Function;
}

@subclass('Landing.widgets.IFrameAppPreview')
export default class IFrameAppPreview extends Widget {

  @property()
  appTypeInfo: AppTypeInfo;

  @property()
  resourceInfo: ResourceInfo;

  @property()
  publishAppVM: PublishAppViewModel;

  @property()
  previewType: previewType;

  @property()
  existingApp: __esri.PortalItem;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages;

  @property()
  publishOverlay: PublishForm;

  @property()
  sendFocusOnClose: Function;

  @property()
  createButton: HTMLCalciteButtonElement;

  @property()
  isOverlayShowing: boolean = true;
  private _finalUrl: string;

  constructor(properties?: IFrameAppPreviewProperties) {
    super(properties);
  }

  postInitialize() {
    this._decidePreviewUrl();

    this.watch("isOverlayShowing", () => {
      this.scheduleRender();
    });
  }

  render() {
    const { appPreviewModal, previewButtons } = this.appMessages;
    return (
      <calcite-modal
        class="iframe-modal"
        afterCreate={(modal: HTMLCalciteModalElement) => {
          applyShadowStyle(SHADOW_STYLE_PREVIEW_MODAL, modal);
          this._handleModalClose(modal);
        }}
        active={true}
        fullscreen="true"
        color="null"
        background-color="white"
        scale="m"
        width="s"
        intl-close={appPreviewModal.close}
      >
        <h3 slot="header" class="iframe-modal__header">
          {previewButtons[this.previewType]} - {this.appTypeInfo.appTypeTitle}
        </h3>

        <div class="iframe-modal__app-preview-div" slot="content">

          <iframe
            bind={this}
            class="iframe-app-preview"
            data-embed-type="instant-gallery"
            src={this._finalUrl}
            width="100%"
            height="100%"
            frameborder="0" />

          <div class="publish-overlay">
            {this.publishOverlay?.render()}
          </div>

        </div>
        <calcite-button
          slot="secondary"
          scale="l"
          width="full"
          appearance="outline"
          onclick={this.close.bind(this)}
        >
          {appPreviewModal.close}
        </calcite-button>
        <calcite-button
          bind={this}
          afterCreate={storeNode}
          data-node-ref={"createButton"}
          slot="primary"
          scale="l"
          width="full"
          disabled={this.isOverlayShowing === false}
          onclick={this._initPublishOverlay.bind(this)}
        >
          {appPreviewModal.choose}
        </calcite-button>
      </calcite-modal>
    );
  }

  close(): void {
    this.sendFocusOnClose?.();
    this.destroy();
    this.scheduleRender();
  }

  private _handleModalClose(modal: HTMLCalciteModalElement) {
    modal.addEventListener("calciteModalClose", () => {
      this.destroy();
    });
  }

  private _initPublishOverlay() {
    const { publishAppVM, appTypeInfo, resourceInfo, existingApp } = this;
    this.isOverlayShowing = false;

    if (existingApp == null) {
      // display normal preview publishForm
      this.publishOverlay = new PublishForm({
        ui: "positionedOverlay",
        mode: "newApp",
        publishAppVM,
        appTypeInfo,
        resourceInfo,
        feedbackFunction: (msg: string, success: boolean, appId?: string) => {
          this.publishAppVM.myAppsDataVM.queryAllMyApps();
        },
        updateFunction: () => { this.isOverlayShowing = true; },
        sendFocusOnClose: () => {
          setTimeout(() => {
            focusElement.call(this, this.createButton);
          }, 200)
        }
      });
    } else {
      // display based-off-existing-app preview publishForm
      this.publishOverlay = new PublishForm({
        ui: "positionedOverlay",
        mode: "fromExistingApp",
        appTypeInfo,
        existingAppItem: this.existingApp,
        resourceInfo,

        publishAppVM,
        feedbackFunction: (msg: string, success: boolean, appId?: string) => {
          this.publishAppVM.myAppsDataVM.queryAllMyApps();
        },
        updateFunction: () => { this.isOverlayShowing = true; },
        sendFocusOnClose: () => {
          setTimeout(() => {
            focusElement.call(this, this.createButton);
          }, 200)
        }

      });
    }
  }

  private _decidePreviewUrl() {
    const { appTypeUrl, appTypeUrlFragment } = this.appTypeInfo;
    const locale = getLocale();
    const appIdStr = this._decideAppId();
    if (this.previewType === "mapPreview") {
      const { resourceItem, resourceType } = this.resourceInfo;
      const resourceStr = this._decideResourceTypeStr(resourceType);
      this._finalUrl = `${appTypeUrl}?${appIdStr}locale=${locale}&${resourceStr}=${resourceItem.id}`;
    } else { // sample preview
      const env = findEnvironmentByUrl();

      switch (appTypeUrlFragment) {
        case EAppTemplateType.CategoryGallery:
          this._finalUrl = `${appTypeUrl}?${appIdStr}locale=${locale}&group=${staticAppConfig[env].sample_CateGallery}`;
          break;
        default:
          if (appIdStr !== "") { // if app id exists then use the org that the appId is from
            this._finalUrl = `${appTypeUrl}?${appIdStr}locale=${locale}`;
          } else { // if app id doesn't exist then use arcgis.com
            this._finalUrl = `https://arcgis.com${appTypeUrlFragment}?locale=${locale}`;
          }
          break;
      }
    }
  }

  private _decideAppId(): string {
    return this.existingApp != null ? `appid=${this.existingApp.id}&` : "";
  }

  private _decideResourceTypeStr(resourceType: EResourceType): string {
    if (resourceType === EResourceType.Webmap) {
      return "webmap";
    } else if (resourceType === EResourceType.Webscene) {
      return "webscene";
    } else if (resourceType === EResourceType.Group) {
      return "group";
    } else {
      return "resourceType";
    }
  }
}
